import { useLocation } from "react-router-dom";
import i18n from "@/i18n/config";
import { SeoLang, SUPPORTED_SEO_LANGS } from "@/lib/seo-config";
import { urlFor, getDomainConfig, PATH_BY_LANG } from "@/lib/domain-config";

const PREF_KEY = "td_lang_pref";

/**
 * Returns a language-change handler. Behaviour depends on whether the new
 * language belongs to the *current* domain or a different ccTLD:
 *
 * - Same domain (e.g. switching from English to Spanish on relark.app):
 *   stay on this domain, just navigate to the language path prefix.
 * - Cross-domain (e.g. Danish from relark.app → relark.dk): full page
 *   navigation to the canonical ccTLD so the URL matches the locale.
 *
 * For non-landing pages (/features, /pricing, /auth), we navigate to the
 * same path on the destination domain so the user stays in context.
 */
export function useLanguageSwitcher() {
  const location = useLocation();

  return (lang: string) => {
    i18n.changeLanguage(lang);
    document.documentElement.lang = lang;
    try { localStorage.setItem(PREF_KEY, lang); } catch {}

    if (!(SUPPORTED_SEO_LANGS as string[]).includes(lang)) return;
    const seoLang = lang as SeoLang;

    const currentDomain = getDomainConfig();
    const path = location.pathname;

    // Recognise legacy language-prefixed landing paths (/en, /no, …) so we
    // can map them back to "/" when staying on the same host.
    const isLegacyLandingPath = ["/", "/en", "/no", "/sv", "/de", "/fr", "/es", "/pt"].includes(path);

    if (isLegacyLandingPath) {
      // Always go to the lang's canonical ccTLD root.
      window.location.href = urlFor(seoLang);
      return;
    }

    // Sub-page (pricing, features, …) — navigate to the same path on the
    // destination ccTLD so the user keeps their context.
    const targetOrigin = urlFor(seoLang).replace(/\/$/, "");
    const langPathPrefix = PATH_BY_LANG[seoLang];
    // Strip any leading legacy /xx prefix from the current path
    const cleanPath = path.replace(/^\/(en|no|sv|de|fr|es|pt)(?=\/|$)/, "") || "/";
    const finalPath =
      langPathPrefix === "/" ? cleanPath : `${langPathPrefix}${cleanPath === "/" ? "" : cleanPath}`;

    if (`https://${currentDomain.hostname}` === targetOrigin) {
      // Same domain — soft navigate
      window.history.replaceState({}, "", finalPath);
      window.dispatchEvent(new PopStateEvent("popstate"));
      return;
    }
    // Cross-domain — hard navigation
    window.location.href = `${targetOrigin}${finalPath === "/" ? "" : finalPath}`;
  };
}
